import { useEffect, useMemo, useState } from 'react';
import { Calendar, Clock3, Home, LogOut, RefreshCw, X } from 'lucide-react';
import * as api from '../../../utils/api';
import type { Project } from '../../../types/project';
import type { Operator, TimeEntry } from '../../../types/timeTracking';
import type { MicrosoftAccount } from '../PINCodesScreen';

type TimeframeMode = 'week' | 'month' | 'custom';
type PeopleTab = 'operators' | 'supervisors';

interface TimesheetsScreenProps {
  operators: Operator[];
  projects: Project[];
  microsoftAccounts?: MicrosoftAccount[];
  onGoHome?: () => void;
  onLogout?: () => void;
  userName?: string;
  hidePeopleTabs?: boolean;
}

interface CellDetailState {
  employeeId: string;
  date: string;
}

const TIME_ENTRIES_STORAGE_KEY = 'timeEntries';

const readLocalTimeEntries = (): TimeEntry[] => {
  try {
    const stored = localStorage.getItem(TIME_ENTRIES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const getLocalDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatHours = (hours: number) => {
  if (hours <= 0) return '0 sec';

  const totalSeconds = Math.max(0, Math.round(hours * 3600));
  const wholeHours = Math.floor(totalSeconds / 3600);
  const wholeMinutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (wholeHours > 0) {
    if (wholeMinutes === 0 && seconds === 0) return `${wholeHours} hr`;
    if (seconds === 0) return `${wholeHours} hr ${wholeMinutes}m`;
    return `${wholeHours} hr ${wholeMinutes}m ${seconds} sec`;
  }

  if (wholeMinutes > 0) {
    if (seconds === 0) return `${wholeMinutes}m`;
    return `${wholeMinutes}m ${seconds} sec`;
  }

  return `${seconds} sec`;
};

const formatDateLabel = (date: Date) =>
  date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

export function TimesheetsScreen({ operators, projects, microsoftAccounts, onGoHome, onLogout, userName, hidePeopleTabs }: TimesheetsScreenProps) {
  const [peopleTab, setPeopleTab] = useState<PeopleTab>('operators');
  const [timeframe, setTimeframe] = useState<TimeframeMode>('week');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailCell, setDetailCell] = useState<CellDetailState | null>(null);
  const [tick, setTick] = useState(Date.now());

  useEffect(() => {
    const interval = window.setInterval(() => setTick(Date.now()), 60000);
    return () => window.clearInterval(interval);
  }, []);

  const loadTimeEntries = async () => {
    try {
      setLoading(true);
      const loaded = await api.getTimeEntries();
      setTimeEntries((loaded && loaded.length > 0) ? loaded : readLocalTimeEntries());
    } catch (error) {
      console.error('Error loading time entries:', error);
      setTimeEntries(readLocalTimeEntries());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadTimeEntries();
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      void loadTimeEntries();
    }, 30000);
    return () => window.clearInterval(interval);
  }, []);

  const dateRange = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dates: Date[] = [];

    if (timeframe === 'custom' && customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start <= end) {
        const current = new Date(start);
        while (current <= end) {
          dates.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }
      }
      return dates;
    }

    if (timeframe === 'month') {
      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        dates.push(date);
      }
      return dates;
    }

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      dates.push(date);
    }
    return dates;
  }, [timeframe, customStartDate, customEndDate]);

  // Derive supervisor list from entries tagged with roleType === 'supervisor'
  const supervisorEntries = timeEntries.filter(e => e.roleType === 'supervisor');
  const supervisorIds = [...new Set(supervisorEntries.map(e => e.employeeId))];
  const supervisors = supervisorIds.map(id => {
    const entry = supervisorEntries.find(e => e.employeeId === id)!;
    const account = microsoftAccounts?.find(a => a.id === id);
    return { id, email: entry.employeeEmail, name: entry.employeeName, regularHoursPerWeek: account?.regularHoursPerWeek ?? 40 };
  });

  const operatorTimeEntries = timeEntries.filter(e => !e.roleType || e.roleType === 'operator');
  const activeEntries = peopleTab === 'operators' ? operatorTimeEntries : supervisorEntries;

  // If the operators prop covers the entries, use it; otherwise derive from the entries themselves
  const derivedOperatorPeople = operators.length > 0
    ? operators.map(op => ({ id: op.id, email: op.email, name: op.email, regularHoursPerWeek: op.regularHoursPerWeek }))
    : [...new Set(operatorTimeEntries.map(e => e.employeeId))].map(id => {
        const e = operatorTimeEntries.find(x => x.employeeId === id)!;
        return { id, email: e.employeeEmail, name: e.employeeName, regularHoursPerWeek: 40 };
      });

  const activePeople = peopleTab === 'operators' ? derivedOperatorPeople : supervisors;

  const entriesByEmployeeAndDate = useMemo(() => {
    const map = new Map<string, TimeEntry[]>();
    activeEntries.forEach((entry) => {
      const key = `${entry.employeeId}::${entry.date}`;
      const current = map.get(key) || [];
      current.push(entry);
      map.set(key, current);
    });
    return map;
  }, [activeEntries]);

  const getEntryHours = (entry: TimeEntry) => {
    if (entry.totalHours != null && entry.totalHours > 0) return entry.totalHours;
    if (entry.clockOutAt) {
      return Math.max(0, (new Date(entry.clockOutAt).getTime() - new Date(entry.clockInAt).getTime()) / 3600000);
    }
    if (entry.status === 'open') {
      return (tick - new Date(entry.clockInAt).getTime()) / 3600000;
    }
    return 0;
  };

  const getCellEntries = (employeeId: string, date: string) =>
    entriesByEmployeeAndDate.get(`${employeeId}::${date}`) || [];

  const getCellHours = (employeeId: string, date: string) =>
    getCellEntries(employeeId, date).reduce((sum, entry) => sum + getEntryHours(entry), 0);

  const visibleDateKeys = useMemo(() => dateRange.map(date => getLocalDateKey(date)), [dateRange]);

  const getVisibleTotalHours = (employeeId: string) =>
    visibleDateKeys.reduce((sum, dateKey) => sum + getCellHours(employeeId, dateKey), 0);

  const getWeekBucket = (dateKey: string) => {
    const date = new Date(`${dateKey}T00:00:00`);
    const day = date.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    date.setDate(date.getDate() + diffToMonday);
    return getLocalDateKey(date);
  };

  const getVisibleOvertimeHours = (person: { id: string; regularHoursPerWeek: number }) => {
    const weeklyTotals = new Map<string, number>();
    visibleDateKeys.forEach(dateKey => {
      const hours = getCellHours(person.id, dateKey);
      if (hours <= 0) return;
      const weekKey = getWeekBucket(dateKey);
      weeklyTotals.set(weekKey, (weeklyTotals.get(weekKey) || 0) + hours);
    });
    let overtime = 0;
    weeklyTotals.forEach(total => {
      overtime += Math.max(0, total - person.regularHoursPerWeek);
    });
    return overtime;
  };

  const detailEntries = detailCell ? getCellEntries(detailCell.employeeId, detailCell.date) : [];

  return (
    <div className={`space-y-6 ${onGoHome ? 'min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6' : 'p-6'}`}>
      {/* Nav header — only shown in user (non-admin) view */}
      {onGoHome && (
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button
              onClick={onGoHome}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 bg-white/80 hover:bg-white rounded-lg transition shadow-sm hover:shadow-md"
            >
              <Home className="w-5 h-5" />
              <span>Home</span>
            </button>
            {userName && (
              <div className="px-4 py-2 bg-white rounded-lg shadow-sm">
                <span className="text-sm text-gray-600">Logged in as </span>
                <span className="text-sm font-semibold text-indigo-700">{userName}</span>
              </div>
            )}
          </div>
          {onLogout && (
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 bg-white/80 hover:bg-white rounded-lg transition shadow-sm hover:shadow-md"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          )}
        </div>
      )}

      {/* People tabs — hidden in operator self-view */}
      {!hidePeopleTabs && (
        <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
          <button
            onClick={() => setPeopleTab('operators')}
            className={`px-5 py-2 rounded-md text-sm font-medium transition ${peopleTab === 'operators' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Operators
          </button>
          <button
            onClick={() => setPeopleTab('supervisors')}
            className={`px-5 py-2 rounded-md text-sm font-medium transition ${peopleTab === 'supervisors' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Supervisors
          </button>
        </div>
      )}

      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Timesheets</h2>
          <p className="text-gray-600 mt-1">
            {peopleTab === 'operators' ? 'Operator' : 'Supervisor'} rows show total worked hours per day. Click a cell to see details.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setTimeframe('week')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${timeframe === 'week' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
          >
            Week
          </button>
          <button
            onClick={() => setTimeframe('month')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${timeframe === 'month' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
          >
            Month
          </button>
          <button
            onClick={() => setTimeframe('custom')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${timeframe === 'custom' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
          >
            Custom
          </button>
          <button
            onClick={() => void loadTimeEntries()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 transition"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {timeframe === 'custom' && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col md:flex-row gap-4 md:items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {peopleTab === 'operators' ? 'Operator' : 'Supervisor'} Timesheet Timeline
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {activePeople.length} {peopleTab === 'operators' ? 'operator' : 'supervisor'}{activePeople.length === 1 ? '' : 's'} • {activeEntries.length} time entr{activeEntries.length === 1 ? 'y' : 'ies'}
            </p>
          </div>
          {loading && <div className="text-sm text-gray-500">Loading timesheets...</div>}
        </div>

        {activePeople.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            {peopleTab === 'operators'
              ? 'No operators have been added yet.'
              : 'No supervisor time entries recorded yet.'}
          </div>
        ) : dateRange.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            Select a valid date range to view timesheets.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-r border-gray-200 min-w-[220px]">
                    {peopleTab === 'operators' ? 'Operator' : 'Supervisor'}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-r border-gray-200 min-w-[110px]">
                    Total
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-r border-gray-200 min-w-[110px]">
                    Overtime
                  </th>
                  {dateRange.map((date) => (
                    <th
                      key={date.toISOString()}
                      className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-r border-gray-200 min-w-[100px]"
                    >
                      <div className="flex flex-col items-center gap-1">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span>{formatDateLabel(date)}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {activePeople.map(person => {
                  const overtime = getVisibleOvertimeHours(person);
                  return (
                    <tr key={person.id} className="hover:bg-gray-50 transition">
                      <td className="sticky left-0 z-10 bg-white px-6 py-4 border-r border-gray-200 align-top">
                        <div className="space-y-1">
                          <div className="font-semibold text-gray-900">{person.name || person.email}</div>
                          <div className="text-xs text-gray-500">{person.email}</div>
                        </div>
                      </td>
                      <td className="px-2 py-3 border-r border-gray-200 text-center">
                        <div className="min-h-[56px] rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 font-semibold flex items-center justify-center">
                          {formatHours(getVisibleTotalHours(person.id))}
                        </div>
                      </td>
                      <td className="px-2 py-3 border-r border-gray-200 text-center">
                        <div className={`min-h-[56px] rounded-lg border font-semibold flex items-center justify-center ${
                          overtime > 0
                            ? 'border-amber-200 bg-amber-50 text-amber-700'
                            : 'border-gray-200 bg-gray-50 text-gray-400'
                        }`}>
                          {formatHours(overtime)}
                        </div>
                      </td>
                      {dateRange.map(date => {
                        const dateKey = getLocalDateKey(date);
                        const totalHours = getCellHours(person.id, dateKey);
                        const hasEntries = totalHours > 0;
                        return (
                          <td key={`${person.id}-${dateKey}`} className="px-2 py-3 border-r border-gray-200 text-center">
                            <button
                              onClick={() => setDetailCell({ employeeId: person.id, date: dateKey })}
                              className={`w-full min-h-[56px] rounded-lg border text-sm font-semibold transition ${
                                hasEntries
                                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                                  : 'bg-gray-50 border-gray-200 text-gray-400 hover:bg-gray-100'
                              }`}
                            >
                              {formatHours(totalHours)}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detailCell && (
        <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white border-l border-gray-200 shadow-2xl flex flex-col">
          <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Worked Hours Detail</h3>
              <p className="text-sm text-gray-600 mt-1">{detailCell.date}</p>
            </div>
            <button
              onClick={() => setDetailCell(null)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {detailEntries.length === 0 ? (
              <div className="text-sm text-gray-500">No time was recorded for this operator on this date.</div>
            ) : (
              detailEntries.map(entry => {
                const project = projects.find(item => item.id === entry.projectId);
                return (
                  <div key={entry.id} className="rounded-xl border border-gray-200 p-4 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold text-gray-900">{project?.name || entry.projectId}</div>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${entry.status === 'open' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {entry.status === 'open' ? 'Open' : 'Closed'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <div>Clock in: {new Date(entry.clockInAt).toLocaleString()}</div>
                      <div>Clock out: {entry.clockOutAt ? new Date(entry.clockOutAt).toLocaleString() : 'Still clocked in'}</div>
                    </div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-indigo-700">
                      <Clock3 className="w-4 h-4" />
                      {formatHours(entry.totalHours ?? ((Date.now() - new Date(entry.clockInAt).getTime()) / 3600000))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
